import React, { useState, useEffect, useRef } from 'react';
import { Box, LinearProgress, Typography, Alert } from '@mui/material';
import { apiService } from '../../services/apiService';

interface ImportSwaggerAssertionsProps {
  projectId: number;
  dataSetsCreated: number;
  onComplete: (assertionsCreated: number) => void;
  onError: (error: string) => void;
}

const ImportSwaggerAssertions: React.FC<ImportSwaggerAssertionsProps> = ({
  projectId,
  onComplete,
  onError,
}) => {
  const [progress, setProgress] = useState(5);
  const [status, setStatus] = useState<string>('Creating assertions…');
  const [assertionsCreated, setAssertionsCreated] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const createAssertions = async () => {
      try {
        if (!projectId || projectId <= 0) {
          const errorMsg = 'Project ID is required';
          if (isMountedRef.current) {
            setError(errorMsg);
            onError(errorMsg);
          }
          return;
        }

        pollTimer = setInterval(() => {
          void (async () => {
            try {
              const live = await apiService.getSwaggerImportProgress(projectId);
              if (!isMountedRef.current) return;
              if (typeof live.progressPercent === 'number') {
                setProgress(live.progressPercent);
              }
              if (live.progressLabel) {
                setStatus(live.progressLabel);
              }
              if (typeof live.assertionsCreated === 'number') {
                setAssertionsCreated(live.assertionsCreated);
              }
            } catch {
              /* keep last UI while the bulk request is still running */
            }
          })();
        }, 1000);

        const result = await apiService.createSwaggerImportAssertions(projectId);
        if (!isMountedRef.current) return;
        const created = result.assertionsCreated ?? 0;
        setAssertionsCreated(created);
        setProgress(100);
        setStatus(
          created > 0
            ? `Successfully created ${created} assertion(s)`
            : 'No new assertions created',
        );
        onComplete(created);
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
            ?.message ||
          (err as { message?: string })?.message ||
          'Failed to create assertions';
        if (isMountedRef.current) {
          setError(errorMessage);
          setStatus('Error creating assertions');
          onError(errorMessage);
        }
      } finally {
        if (pollTimer != null) {
          clearInterval(pollTimer);
        }
      }
    };

    void createAssertions();

    return () => {
      isMountedRef.current = false;
      if (pollTimer != null) {
        clearInterval(pollTimer);
      }
    };
  }, [projectId, onComplete, onError]);

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 700 }}>
        Creating Assertions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {status}
        </Typography>
        <Typography variant="body2" color="success.main">
          Assertions created: {assertionsCreated}
        </Typography>
      </Box>

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />

      <Typography variant="caption" color="text.secondary">
        {Math.round(progress)}% complete
      </Typography>
    </Box>
  );
};

export default ImportSwaggerAssertions;
